import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../api/axios';
import Modal from '../components/shared/Modal';
import ConfirmModal from '../components/shared/ConfirmModal';
import { format } from 'date-fns';

export default function Clients() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [name, setName] = useState('');

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients-all'],
    queryFn: () => api.get('/clients/all').then(r => r.data),
  });

  const saveMut = useMutation({
    mutationFn: () => editClient
      ? api.put(`/clients/${editClient.id}`, { name })
      : api.post('/clients', { name }),
    onSuccess: () => {
      toast.success(editClient ? 'Client updated' : 'Client added');
      qc.invalidateQueries(['clients-all']);
      qc.invalidateQueries(['clients']);
      setModalOpen(false);
    },
  });

  const deleteMut = useMutation({
    mutationFn: id => api.delete(`/clients/${id}`),
    onSuccess: () => { toast.success('Client deleted'); qc.invalidateQueries(['clients-all']); qc.invalidateQueries(['clients']); },
  });

  const uploadMut = useMutation({
    mutationFn: ({ file, text }) => {
      const fd = new FormData();
      if (file) fd.append('file', file);
      if (text) fd.append('names', text);
      return api.post('/clients/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    onSuccess: r => {
      toast.success(r.data.message);
      qc.invalidateQueries(['clients-all']);
      qc.invalidateQueries(['clients']);
      setUploadOpen(false);
      setPasteText('');
    },
  });

  const toggleActive = c => api.put(`/clients/${c.id}`, { is_active: c.is_active ? 0 : 1 })
    .then(() => { qc.invalidateQueries(['clients-all']); qc.invalidateQueries(['clients']); });

  return (
    <div>
      <div className="page-header">
        <h2>Client Management</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => setUploadOpen(true)}>
            ↑ Upload CSV
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => { setEditClient(null); setName(''); setModalOpen(true); }}>
            + Add Client
          </button>
        </div>
      </div>

      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Client Name</th><th>Jobs</th><th>Added</th><th>Active</th><th>Actions</th></tr></thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30 }}>Loading…</td></tr>
            ) : clients.map(c => (
              <tr key={c.id}>
                <td><strong>{c.name}</strong></td>
                <td><span style={{ fontSize: 11, background: '#e8f3fb', color: '#0a5d94', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{c.job_count}</span></td>
                <td style={{ color: '#6c757d', fontSize: 12 }}>{c.created_at ? format(new Date(c.created_at), 'dd MMM yyyy') : '—'}</td>
                <td><div className={`toggle ${c.is_active ? 'on' : ''}`} onClick={() => toggleActive(c)} role="presentation" /></td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button type="button" className="ico-btn" onClick={() => { setEditClient(c); setName(c.name); setModalOpen(true); }}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M11 2l3 3-9 9H2v-3l9-9z" /></svg>
                    </button>
                    <button type="button" className="ico-btn" onClick={() => setDeleteId(c.id)}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#dc3545" strokeWidth="1.5"><polyline points="2,4 14,4" /><path d="M5 4V2h6v2M6 7v5M10 7v5" /><rect x="3" y="4" width="10" height="10" rx="1" /></svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editClient ? 'Edit Client' : 'Add Client'}>
        <div className="form-group"><label>Client Name</label><input value={name} onChange={e => setName(e.target.value)} placeholder="Enter client name" /></div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-outline" onClick={() => setModalOpen(false)}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={() => saveMut.mutate()} disabled={!name || saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : 'Save Client'}
          </button>
        </div>
      </Modal>

      <Modal open={uploadOpen} onClose={() => setUploadOpen(false)} title="Upload Client List" width={480}>
        <p style={{ fontSize: 12, color: '#6c757d', marginBottom: 14 }}>Upload a CSV file with client names (one per row), or paste names below.</p>
        <div role="button" tabIndex={0} onClick={() => fileRef.current?.click()} onKeyDown={e => { if (e.key === 'Enter') fileRef.current?.click(); }}
          style={{ border: '2px dashed #ced4da', borderRadius: 8, padding: 28, textAlign: 'center', cursor: 'pointer', marginBottom: 14 }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#0f74b9'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#ced4da'; }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0f74b9" strokeWidth="1.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17,8 12,3 7,8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
          <p style={{ fontSize: 13, color: '#6c757d', marginTop: 8 }}>Click to upload CSV</p>
          <small style={{ fontSize: 11, color: '#ced4da' }}>One client name per row</small>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }}
            onChange={e => { if (e.target.files[0]) uploadMut.mutate({ file: e.target.files[0] }); }} />
        </div>
        <div className="form-group">
          <label>Or paste names (one per line)</label>
          <textarea value={pasteText} onChange={e => setPasteText(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ced4da', borderRadius: 6, fontSize: 12, minHeight: 90 }}
            placeholder={'ABC Corp\nXYZ Ltd\nBright Co'} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-outline" onClick={() => setUploadOpen(false)}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!pasteText || uploadMut.isPending}
            onClick={() => uploadMut.mutate({ text: pasteText })}>
            {uploadMut.isPending ? 'Importing…' : 'Import Clients'}
          </button>
        </div>
      </Modal>

      <ConfirmModal open={!!deleteId} onClose={() => setDeleteId(null)}
        title="Delete this client?" message="Clients with existing jobs cannot be deleted."
        confirmLabel="Delete" danger onConfirm={() => deleteMut.mutate(deleteId)} />
    </div>
  );
}
